/**
 * Identity colours for calendar chips.
 *
 * `getCalendarEventColor` hashes an id across 30 fully-saturated hues — pure
 * red, pure amber, near-black greys — which on a dense month reads as noise and
 * fights the app's indigo. This palette is the same idea at a calmer volume: a
 * short set of hues that sit together, each one held to 3:1 as a 3px rail and
 * as a small icon — per theme, which is why the values are tokens and not
 * literals (see CHIP_COLORS below).
 *
 * Selection stays a deterministic hash, so a campaign keeps its colour between
 * renders and between sessions, which is the whole point of an identity colour.
 */
/* The values live in the token system (`color-scheme-tokens.css`), not here.
 * A hex literal in JS can only ever be right for ONE theme, and these are used
 * as graphics — a 3px rail, the kind glyph in a chip, the glyph in an avatar
 * placeholder on a 14% tint of itself. Eight of the thirteen Tailwind-500s that
 * used to sit here were under 3:1 on white (lime 1.98, amber 2.15, cyan 2.43,
 * teal 2.49, emerald 2.54, sky 2.77, orange 2.80). Light now darkens and dark
 * lightens, per token, with every ratio recorded next to its value.
 *
 * Hue and order are unchanged, so an id keeps the colour it has always had. */
const CHIP_COLORS = [
    'var(--cal-id-1)',   // indigo — the brand hue leads
    'var(--cal-id-2)',   // sky
    'var(--cal-id-3)',   // teal
    'var(--cal-id-4)',   // emerald
    'var(--cal-id-5)',   // lime
    'var(--cal-id-6)',   // amber
    'var(--cal-id-7)',   // yellow  (was red-orange — red means failed)
    'var(--cal-id-8)',   // blue    (was red)
    'var(--cal-id-9)',   // forest  (was pink)
    'var(--cal-id-10)',  // purple
    'var(--cal-id-11)',  // violet
    'var(--cal-id-12)',  // cyan
];

const NEUTRAL = 'var(--cal-id-neutral)';

function hash(value: string): number {
    let h = 0;
    for (let i = 0; i < value.length; i++) {
        h = ((h << 5) - h) + value.charCodeAt(i);
        h |= 0;
    }
    return Math.abs(h);
}

/** Deterministic identity colour for a campaign / drip / message id. */
export function chipColorFor(id: string | undefined | null): string {
    if (!id) return NEUTRAL;
    return CHIP_COLORS[hash(id) % CHIP_COLORS.length];
}

export const CHIP_PALETTE = CHIP_COLORS;
